import {
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {createPortal} from "react-dom";
import styles from "./BaseSelect.module.css";

export interface SelectOption<T extends string | number = string | number> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface BaseSelectProps<T extends string | number = string | number> {
  options: SelectOption<T>[];
  value?: T | null;
  defaultValue?: T;
  placeholder?: string;
  onChange?: (value: T, option: SelectOption<T>) => void;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  error?: string;
  label?: string;
  id?: string;
  className?: string;
}

/* ── SVG Icons ── */

const ChevronIcon: FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2 4l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClearIcon: FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2 2l8 8M10 2L2 10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const SpinnerIcon: FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    className={styles.spinnerIcon}
    aria-hidden="true"
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

/* ── Dropdown styles calculated from trigger DOM rect ── */

interface DropdownPosition {
  position: "fixed";
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  zIndex: number;
}

const DROPDOWN_ESTIMATED_HEIGHT = 292; // max-height + border
const DROPDOWN_GAP = 2;

const computeDropdownPosition = (trigger: HTMLElement): {pos: DropdownPosition; upward: boolean} => {
  const rect = trigger.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const upward = spaceBelow < DROPDOWN_ESTIMATED_HEIGHT && rect.top > spaceBelow;

  return {
    pos: {
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      ...(upward ? {bottom: viewportHeight - rect.top + DROPDOWN_GAP} : {top: rect.bottom + DROPDOWN_GAP}),
    },
    upward,
  };
};

/* ── Component ── */

const BaseSelect = <T extends string | number = string | number>({
  options,
  value,
  defaultValue,
  placeholder = "Select an option",
  onChange,
  disabled = false,
  loading = false,
  searchable = false,
  clearable = false,
  size = "md",
  fullWidth = true,
  error,
  label,
  id,
  className,
}: BaseSelectProps<T>) => {
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<T | null>(defaultValue ?? null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);
  const [openUpward, setOpenUpward] = useState(false);

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedValue = isControlled ? value : internalValue;
  const selectedOption = options.find(opt => opt.value === selectedValue) ?? null;

  const filteredOptions =
    searchable && search.trim()
      ? options.filter(opt => opt.label.toLowerCase().includes(search.trim().toLowerCase()))
      : options;

  /* ── Position calculation ── */
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const {pos, upward} = computeDropdownPosition(triggerRef.current);
    setDropdownPos(pos);
    setOpenUpward(upward);
  }, []);

  /* ── Open / close ── */
  const open = useCallback(() => {
    if (disabled || loading) return;
    updatePosition();
    setIsOpen(true);
    setHighlightedIndex(selectedOption ? options.indexOf(selectedOption) : -1);
    if (searchable) setTimeout(() => searchRef.current?.focus(), 0);
  }, [disabled, loading, updatePosition, searchable, selectedOption, options]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch("");
    setHighlightedIndex(-1);
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  /* ── Select option ── */
  const selectOption = useCallback(
    (option: SelectOption<T>) => {
      if (option.disabled) return;
      if (!isControlled) setInternalValue(option.value);
      onChange?.(option.value, option);
      close();
      triggerRef.current?.focus();
    },
    [isControlled, onChange, close]
  );

  /* ── Clear ── */
  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!isControlled) setInternalValue(null);
      onChange?.(null as unknown as T, null as unknown as SelectOption<T>);
    },
    [isControlled, onChange]
  );

  /* ── Keyboard navigation on trigger ── */
  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          if (!isOpen) open();
          else if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            selectOption(filteredOptions[highlightedIndex]);
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          if (!isOpen) {
            open();
          } else {
            setHighlightedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (isOpen) setHighlightedIndex(i => Math.max(i - 1, 0));
          break;
        case "Escape":
          close();
          break;
        case "Tab":
          close();
          break;
      }
    },
    [disabled, isOpen, open, close, selectOption, highlightedIndex, filteredOptions]
  );

  /* ── Keyboard navigation in search input ── */
  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex(i => Math.max(i - 1, 0));
          break;
        case "Enter":
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            selectOption(filteredOptions[highlightedIndex]);
          }
          break;
        case "Escape":
          close();
          triggerRef.current?.focus();
          break;
      }
    },
    [filteredOptions, highlightedIndex, selectOption, close]
  );

  /* ── Click outside ── */
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: globalThis.MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node) && !dropdownRef.current?.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, close]);

  /* ── Reposition on scroll / resize ── */
  useEffect(() => {
    if (!isOpen) return;
    const onUpdate = () => updatePosition();
    window.addEventListener("scroll", onUpdate, {capture: true, passive: true});
    window.addEventListener("resize", onUpdate, {passive: true});
    return () => {
      window.removeEventListener("scroll", onUpdate, {capture: true});
      window.removeEventListener("resize", onUpdate);
    };
  }, [isOpen, updatePosition]);

  /* ── Scroll highlighted option into view ── */
  useLayoutEffect(() => {
    if (highlightedIndex < 0 || !dropdownRef.current) return;
    const items = dropdownRef.current.querySelectorAll<HTMLElement>("[data-option]");
    items[highlightedIndex]?.scrollIntoView({block: "nearest"});
  }, [highlightedIndex]);

  /* ── Build trigger class ── */
  const triggerClass = [
    styles.trigger,
    styles[`size_${size}`],
    isOpen ? styles.open : "",
    disabled ? styles.disabled : "",
    error ? styles.error : "",
    loading ? styles.loading : "",
  ]
    .filter(Boolean)
    .join(" ");

  /* ── Dropdown portal ── */
  const dropdown =
    isOpen && dropdownPos
      ? createPortal(
          <div
            ref={dropdownRef}
            className={[styles.dropdown, openUpward ? styles.dropdownUpward : ""].filter(Boolean).join(" ")}
            style={dropdownPos}
            role="listbox"
            aria-label={label ?? placeholder}
          >
            {searchable && (
              <div className={styles.searchWrapper}>
                <input
                  ref={searchRef}
                  className={styles.searchInput}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search..."
                  aria-label="Search options"
                  autoComplete="off"
                />
              </div>
            )}
            {filteredOptions.length === 0 ? (
              <div className={styles.noOptions}>No options</div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  data-option
                  className={[
                    styles.option,
                    option.value === selectedValue ? styles.optionSelected : "",
                    index === highlightedIndex ? styles.optionHighlighted : "",
                    option.disabled ? styles.optionDisabled : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => selectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="option"
                  aria-selected={option.value === selectedValue}
                  aria-disabled={option.disabled}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div className={[styles.wrapper, fullWidth ? styles.fullWidth : "", className ?? ""].filter(Boolean).join(" ")}>
      {label && (
        <label
          className={styles.label}
          htmlFor={id}
        >
          {label}
        </label>
      )}

      <div
        ref={triggerRef}
        id={id}
        className={triggerClass}
        onClick={toggle}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label ?? placeholder}
        aria-disabled={disabled}
        aria-invalid={!!error}
      >
        <span className={selectedOption ? styles.value : styles.placeholder}>
          {selectedOption?.label ?? placeholder}
        </span>

        {loading ? (
          <span className={styles.iconRight}>
            <SpinnerIcon />
          </span>
        ) : clearable && selectedValue != null ? (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={handleClear}
            aria-label="Clear selection"
            tabIndex={-1}
          >
            <ClearIcon />
          </button>
        ) : (
          <span className={styles.iconRight}>
            <ChevronIcon />
          </span>
        )}
      </div>

      {error && (
        <span
          className={styles.errorMsg}
          role="alert"
        >
          {error}
        </span>
      )}

      {dropdown}
    </div>
  );
};

export default BaseSelect;
